import { useEffect, useState, useCallback } from "react";
import api, { apiError, downloadFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ShieldCheck, Users, UserCheck, GraduationCap, ClipboardCheck, ClipboardX,
  Award, XCircle, QrCode, FileDown, Loader2, CheckCircle2, Eye,
} from "lucide-react";
import { toast } from "sonner";

const SIT_BADGE = {
  avaliada: { label: "Avaliada", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", Icon: ClipboardCheck },
  acompanhada: { label: "Acompanhada — Não avaliado", cls: "bg-sky-500/20 text-sky-300 border-sky-500/40", Icon: Eye },
  pendente: { label: "Presença não confirmada", cls: "bg-slate-500/20 text-slate-300 border-slate-500/40", Icon: ClipboardX },
};
const RES_BADGE = {
  APTO: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  "NÃO APTO": "bg-red-500/20 text-red-300 border-red-500/40",
  "Não avaliado": "bg-secondary text-muted-foreground border-border",
};

function Stat({ label, value, hint, icon: Icon, tone = "text-primary", testid }) {
  return (
    <Card data-testid={testid} className="p-3 bg-card border-border">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${tone}`} />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono leading-tight">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-heading font-bold">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </Card>
  );
}

export function BancoCapacitados({ turma }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/capacitados/${turma.id}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get(`/inscricoes?turma_id=${turma.id}&status=pendente`).then((r) => setPendentes(r.data)).catch(() => setPendentes([]));
  }, [turma.id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Card className="p-4 bg-card border-border mb-6 text-sm text-muted-foreground">Carregando Banco de Capacitados…</Card>;

  const ind = data.indicadores;
  const alunos = data.alunos;
  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSel = alunos.length > 0 && sel.length === alunos.length;
  const toggleAll = () => setSel(allSel ? [] : alunos.map((a) => a.aluno_id));

  const setPresenca = async (aluno_id, patch) => {
    try {
      const { data: d } = await api.post(`/capacitados/${turma.id}/presenca`, { aluno_id, ...patch });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const lote = async (patch, msg) => {
    if (!sel.length) { toast.error("Selecione ao menos um militar."); return; }
    setBusy(true);
    try {
      const { data: d } = await api.post(`/capacitados/${turma.id}/presenca-lote`, { aluno_ids: sel, ...patch });
      setData(d); setSel([]);
      const ign = d?.presenca_lote?.ignorados?.length || 0;
      if (ign) toast.warning(`${msg} — ${d.presenca_lote.confirmados} confirmado(s), ${ign} não encontrado(s).`);
      else toast.success(msg);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const aprovarPresente = async (iid) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/capacitados/${turma.id}/inscricao/${iid}/aprovar-presente`);
      setData(d); toast.success("Inscrição aprovada e presença confirmada");
      api.get(`/inscricoes?turma_id=${turma.id}&status=pendente`).then((r) => setPendentes(r.data));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const exportar = (tipo, fmt) => {
    const base = (turma.nome || "turma").replace(/\s/g, "_");
    downloadFile(`/capacitados/${turma.id}/relacao/${tipo}/export/${fmt}`, `MÉTRON_Relacao_${tipo}_${base}.${fmt}`)
      .catch(() => toast.error("Falha ao exportar"));
  };

  const nomeExib = (a) => `${a.grau_nome && a.grau_nome !== "Não se aplica" ? a.grau_nome + " " : ""}${a.nome}`;

  return (
    <div className="mb-6" data-testid="banco-capacitados">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Banco de Capacitados</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Todo militar com presença confirmada é contabilizado como capacitado, mesmo sem avaliação prática. Confirmar presença não atribui nota nem resultado APTO.</p>

      {/* Indicadores separados */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
        <Stat testid="ind-inscritos" label="Inscritos na turma" value={ind.inscritos_roll} icon={Users} tone="text-amber-400" />
        <Stat testid="ind-qr" label="Solicitações QR" value={ind.solicitacoes_qr_total} hint={`${ind.solicitacoes_qr_pendentes} pendente(s)`} icon={QrCode} tone="text-sky-400" />
        <Stat testid="ind-participantes" label="Presença confirmada" value={ind.participantes} icon={UserCheck} tone="text-emerald-400" />
        <Stat testid="ind-concluintes" label="Concluintes" value={ind.concluintes} icon={GraduationCap} tone="text-emerald-400" />
        <Stat testid="ind-avaliados" label="Avaliados" value={ind.avaliados} icon={ClipboardCheck} tone="text-primary" />
        <Stat testid="ind-nao-avaliados" label="Não avaliados" value={ind.nao_avaliados} icon={ClipboardX} tone="text-slate-400" />
        <Stat testid="ind-aptos" label="Aptos" value={ind.aptos} icon={Award} tone="text-emerald-400" />
        <Stat testid="ind-nao-aptos" label="Não aptos" value={ind.nao_aptos} icon={XCircle} tone="text-red-400" />
      </div>

      {/* Exportação da Relação Nominal */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[["participantes", "Relação de Participantes"], ["aptos", "Relação de Aptos"]].map(([tipo, label]) => (
          <DropdownMenu key={tipo}>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary" data-testid={`export-${tipo}-btn`}><FileDown className="h-3.5 w-3.5 mr-1.5" />{label}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border-border">
              <DropdownMenuItem data-testid={`export-${tipo}-docx`} onClick={() => exportar(tipo, "docx")}>Word (.docx)</DropdownMenuItem>
              <DropdownMenuItem data-testid={`export-${tipo}-xlsx`} onClick={() => exportar(tipo, "xlsx")}>Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem data-testid={`export-${tipo}-pdf`} onClick={() => exportar(tipo, "pdf")}>PDF (.pdf)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
      </div>

      {/* Inscrições QR pendentes: aprovar + marcar presente numa ação */}
      {pendentes.length > 0 && (
        <Card className="p-3 bg-card border-border mb-4">
          <div className="text-xs font-medium mb-2 flex items-center gap-1.5"><QrCode className="h-3.5 w-3.5 text-sky-400" />Solicitações QR pendentes ({pendentes.length})</div>
          <div className="space-y-1.5">
            {pendentes.map((i) => (
              <div key={i.id} data-testid={`pendente-${i.id}`} className="flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0 truncate">{i.nome} {i.nome_guerra ? <span className="text-xs text-muted-foreground">"{i.nome_guerra}"</span> : null} · {i.grau_nome || "—"}</div>
                <Button size="sm" disabled={busy} data-testid={`aprovar-presente-${i.id}`} onClick={() => aprovarPresente(i.id)}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Aprovar e marcar presente
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Ações em lote */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox data-testid="sel-todos" checked={allSel} onCheckedChange={toggleAll} /> Selecionar todos
        </label>
        <span className="text-xs text-muted-foreground">{sel.length} selecionado(s)</span>
        <Button size="sm" variant="secondary" disabled={busy || !sel.length} data-testid="lote-presente" onClick={() => lote({ presente: true }, "Presença confirmada em lote")}>Confirmar presença</Button>
        <Button size="sm" variant="secondary" disabled={busy || !sel.length} data-testid="lote-concluiu" onClick={() => lote({ concluiu: true }, "Conclusão registrada em lote")}>Marcar concluído</Button>
        <Button size="sm" variant="outline" disabled={busy || !sel.length} data-testid="lote-ausente" onClick={() => lote({ presente: false, concluiu: false }, "Presença removida em lote")}>Remover presença</Button>
      </div>

      {/* Lista de militares */}
      <Card className="bg-card border-border divide-y divide-border">
        {alunos.length ? alunos.map((a) => {
          const sit = SIT_BADGE[a.situacao_pratica];
          return (
            <div key={a.aluno_id} data-testid={`cap-row-${a.aluno_id}`} className="flex items-center gap-3 px-3 py-2.5">
              <Checkbox data-testid={`cap-sel-${a.aluno_id}`} checked={sel.includes(a.aluno_id)} onCheckedChange={() => toggleSel(a.aluno_id)} />
              <div className="min-w-0 flex-1">
                    <div className="font-medium leading-tight break-words text-sm md:text-base" data-testid={`cap-nome-${a.aluno_id}`}>{nomeExib(a)} {a.nome_guerra && <span className="text-xs text-primary font-mono">"{a.nome_guerra}"</span>}</div>
                <div className="text-xs text-muted-foreground truncate">{[a.rg, a.unidade_nome, a.instituicao_nome].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              <Badge className={`${sit.cls} shrink-0 hidden sm:flex`}><sit.Icon className="h-3 w-3 mr-1" />{sit.label}</Badge>
              <Badge data-testid={`cap-res-${a.aluno_id}`} className={`${RES_BADGE[a.resultado_label]} shrink-0`}>{a.resultado_label}{a.media != null ? ` · ${a.media}` : ""}</Badge>
              <div className="flex items-center gap-3 shrink-0">
                <label className="flex items-center gap-1 text-[11px] cursor-pointer" title="Presença confirmada">
                  <Checkbox data-testid={`cap-presente-${a.aluno_id}`} checked={a.presente} onCheckedChange={(v) => setPresenca(a.aluno_id, { presente: !!v })} />
                  <span className="hidden md:inline">Presente</span>
                </label>
                <label className="flex items-center gap-1 text-[11px] cursor-pointer" title="Concluiu a instrução">
                  <Checkbox data-testid={`cap-concluiu-${a.aluno_id}`} checked={a.concluiu} onCheckedChange={(v) => setPresenca(a.aluno_id, { concluiu: !!v })} />
                  <span className="hidden md:inline">Concluiu</span>
                </label>
              </div>
            </div>
          );
        }) : <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhum militar na turma.</div>}
      </Card>
    </div>
  );
}
